import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { initAnalytics } from './lib/analytics'
import { initWebVitals } from './lib/web-vitals'
import { registerStaleChunkRecovery } from './lib/stale-chunk'

// Lazy-load Sentry after first paint (production only)
if (import.meta.env.PROD && !!import.meta.env.VITE_SENTRY_DSN) {
  const initSentry = () => {
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN || '',
        environment: import.meta.env.MODE,
        // Release matches the build-time SENTRY_RELEASE so Sentry groups errors per release
        // (source maps are discovered publicly — see vite.config.ts).
        release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
        enabled: import.meta.env.PROD && !!import.meta.env.VITE_SENTRY_DSN,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
        tracesSampleRate: 0.2,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
      });

      // ICODE-131: ?sentryTest=<tag> sends a deterministic synthetic error so
      // we can verify the prod ingestion path (DSN routing, release tagging,
      // source-map resolution, alert rules) without breaking the UI. The error
      // is captured via captureException — it does NOT throw and does NOT
      // unmount the React tree. Safe to leave in.
      try {
        const params = new URLSearchParams(window.location.search);
        const tag = params.get('sentryTest');
        if (tag) {
          const err = new Error(`ICODE-131 prod ingestion test [${tag}]`);
          Sentry.captureException(err, {
            tags: { test: 'icode-131', tag },
          });
        }
      } catch {
        // Don't let test-trigger plumbing break the app.
      }
    });
  };
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(initSentry);
  } else {
    setTimeout(initSentry, 1000);
  }
}

initAnalytics()
initWebVitals()

// ICODE-208: recover from stale-chunk-after-deploy dynamic-import failures via
// a single guarded reload (see src/lib/stale-chunk.ts).
registerStaleChunkRecovery()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

// Item 97: Register service worker in production only
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('SW registered:', registration.scope);
      })
      .catch((error) => {
        console.warn('SW registration failed:', error);
      });
  });
}
